import React, { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import { Modal, Grid, Textarea } from "@sumprio/ui";
import ModalPortalRoot from "./ModalPortalRoot";

export interface ConfirmDeleteWithCommentModalProps {
  open: boolean;
  title: string;
  supportingText: ReactNode;
  commentLabel?: string;
  commentPlaceholder?: string;
  requireComment?: boolean;
  onClose: () => void;
  onConfirm: (_comment: string) => void;
  primaryLabel?: string;
  confirmDisabled?: boolean;
}

const DEFAULT_PRIMARY_LABEL = "Yes, Delete";

export default function ConfirmDeleteWithCommentModal({
  open,
  title,
  supportingText,
  commentLabel = "Comment",
  commentPlaceholder = "Add a reason for deletion...",
  requireComment = false,
  onClose,
  onConfirm,
  primaryLabel = DEFAULT_PRIMARY_LABEL,
  confirmDisabled = false,
}: ConfirmDeleteWithCommentModalProps) {
  const [comment, setComment] = useState("");
  const commentRef = useRef("");

  useEffect(() => {
    if (!open) {
      setComment("");
      commentRef.current = "";
    }
  }, [open]);

  const trimmedComment = comment.trim();
  const primaryDisabled = confirmDisabled || (requireComment && trimmedComment.length === 0);

  const handlePrimary = useCallback(() => {
    const text = (commentRef.current ?? comment).trim();
    if (requireComment && text.length === 0) return;
    onConfirm(text);
  }, [comment, onConfirm, requireComment]);

  return (
    <ModalPortalRoot open={open} variant="center">
      <Modal
        variant="center"
        open={open}
        onClose={onClose}
        closeOnBackdropClick={false}
        closeOnEscape={false}
        title={title}
        supportingText={supportingText}
        showFeaturedIcon
        featuredIconVariant="error"
        showCloseButton
        showCancelButton={false}
        showPrimaryButton="error"
        primaryLabel={primaryLabel}
        onPrimary={handlePrimary}
        secondaryLabel="Cancel"
        showSecondaryButton
        onSecondary={onClose}
        showFooterDivider={false}
        showHeaderDivider={false}
        primaryButtonProps={{ size: "xs", disabled: primaryDisabled }}
        secondaryButtonProps={{
          variant: "brand-secondaryGrey",
          size: "xs",
          disabled: confirmDisabled,
        }}
      >
        <Grid display="flex" flexDirection="column" gap="sm" marginTop="md">
          <Textarea
            label={commentLabel}
            placeholder={commentPlaceholder}
            value={comment}
            required={requireComment}
            submitOnEnter={false}
            onChange={(e) => {
              const v = e.target.value;
              commentRef.current = v;
              setComment(v);
            }}
            rows={3}
            size="sm"
          />
        </Grid>
      </Modal>
    </ModalPortalRoot>
  );
}
